import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { QetaGuideButton, isQetaLandingPath } from './QetaGuideButton';

const useStyles = makeStyles(() => ({
  tooltip: {
    fontSize: '14px',
    maxWidth: '400px',
    whiteSpace: 'pre-line',
  },
}));

const IMAGE_UPLOAD_HELP_TEXT = `
Note: External images not supported directly for header image

1. Right-click the external image and save it to your computer so that you can upload it.
2. Take a screenshot of the external image and then upload the saved screenshot.
`.trim();

const HELP_ICON_PATHS = ['/qeta/articles/write'];

type QetaResource = {
  kind: 'question' | 'article';
  id: string;
};

const extractQetaResource = (pathname: string): QetaResource | undefined => {
  const questionMatch = pathname.match(/^\/qeta\/questions\/([^/?#]+)/i);
  if (questionMatch?.[1]) {
    return { kind: 'question', id: questionMatch[1] };
  }

  const articleMatch = pathname.match(/^\/qeta\/articles\/([^/?#]+)/i);
  if (articleMatch?.[1] && articleMatch[1].toLowerCase() !== 'write') {
    return { kind: 'article', id: articleMatch[1] };
  }

  return undefined;
};

const QetaTitle = () => {
  const classes = useStyles();
  const location = useLocation();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [muted, setMuted] = useState(false);
  const [loadingPreference, setLoadingPreference] = useState(false);
  const [savingPreference, setSavingPreference] = useState(false);

  const resource = useMemo(
    () => extractQetaResource(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    document.title = 'Questions';
  }, []);

  const shouldShowIcon = useMemo(
    () => HELP_ICON_PATHS.includes(location.pathname),
    [location.pathname],
  );

  const isLandingPage = useMemo(
    () => isQetaLandingPath(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    let cancelled = false;

    const loadPreference = async () => {
      if (!resource) {
        setMuted(false);
        return;
      }

      setLoadingPreference(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('notifications');
        const res = await fetchApi.fetch(
          `${baseUrl}/teams-preferences/${resource.kind}s/${encodeURIComponent(resource.id)}`,
        );
        if (!res.ok) {
          return;
        }
        const body = (await res.json()) as { muted?: boolean };
        if (!cancelled) {
          setMuted(Boolean(body.muted));
        }
      } catch {
        // non-blocking UI preference fetch
      } finally {
        if (!cancelled) {
          setLoadingPreference(false);
        }
      }
    };

    void loadPreference();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, resource]);

  const handleToggle = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!resource) return;

    const nextMuted = event.target.checked;
    setSavingPreference(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('notifications');
      const res = await fetchApi.fetch(
        `${baseUrl}/teams-preferences/${resource.kind}s/${encodeURIComponent(resource.id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ muted: nextMuted }),
        },
      );

      if (res.ok) {
        setMuted(nextMuted);
      }
    } catch {
      // non-blocking UI preference save
    } finally {
      setSavingPreference(false);
    }
  };

  if (!shouldShowIcon && !resource && !isLandingPage) {
    return null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <QetaGuideButton />

      {resource ? (
        loadingPreference ? (
          <CircularProgress size={18} />
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={muted}
                onChange={handleToggle}
                color="primary"
                disabled={savingPreference}
              />
            }
            label="Mute Teams"
            labelPlacement="start"
          />
        )
      ) : null}

      {shouldShowIcon ? (
        <Tooltip
          title={IMAGE_UPLOAD_HELP_TEXT}
          arrow
          placement="bottom"
          classes={{ tooltip: classes.tooltip }}
        >
          <HelpOutlineIcon style={{ cursor: 'pointer' }} />
        </Tooltip>
      ) : null}
    </div>
  );
};

export default QetaTitle;
